import React, { useMemo, useState } from 'react';
import {
  BsChevronRight, BsChevronDown, BsTable, BsFileEarmarkText,
  BsSearch, BsExclamationTriangle, BsCheckCircle, BsSlashCircle,
} from 'react-icons/bs';
import { fmtNum } from './normalize';
import { algChipStyle, algColor, FAIL_RED } from './algorithmColors';
import { INK, STATUS } from 'Utils/styleTokens';

const MAX_FIELDS = 300;

const pill = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  borderRadius: 999, padding: '1px 8px', fontSize: 12, fontWeight: 500,
};

function StatusChip({ unit, units = 'rows' }) {
  if (unit.level === 'failed') {
    const label = unit.failedRows > 0 ? `${fmtNum(unit.failedRows)} ${units} failed` : 'Errors';
    return (
      <span style={{ ...pill, ...algChipStyle(null, true) }}>
        <BsExclamationTriangle size={12} /> {label}
      </span>
    );
  }
  if (unit.level === 'skipped') {
    return (
      <span style={{ ...pill, backgroundColor: STATUS.warning.bg, color: STATUS.warning.text, border: '1px solid #F3DFA8' }}>
        <BsSlashCircle size={12} /> {fmtNum(unit.skipped)} skipped
      </span>
    );
  }
  return (
    <span style={{ ...pill, backgroundColor: STATUS.success.bg, color: STATUS.success.text, border: '1px solid #BBF0CB' }}>
      <BsCheckCircle size={12} /> Clean
    </span>
  );
}

function AlgChip({ alg, count, failed }) {
  return (
    <span style={{ ...pill, borderRadius: 6, ...algChipStyle(alg, failed) }}>
      {alg}
      <span style={{ opacity: 0.6 }}>×{fmtNum(count)}</span>
      {failed && <BsExclamationTriangle size={11} />}
    </span>
  );
}

function UnitRow({ unit, isFile, isNoSql, verb = 'masked' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('field');

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const match = (f) =>
      !term ||
      (f.field || '').toLowerCase().includes(term) ||
      (f.algorithm || '').toLowerCase().includes(term);
    const list = unit.fields.filter(match);
    if (sort === 'algorithm') {
      list.sort((a, b) => (a.algorithm || '').localeCompare(b.algorithm || '') || (a.field || '').localeCompare(b.field || ''));
    } else {
      list.sort((a, b) => (a.field || '').localeCompare(b.field || ''));
    }
    return list;
  }, [unit, q, sort]);

  const shown = filtered.slice(0, MAX_FIELDS);
  const units = isFile ? 'records' : isNoSql ? 'documents' : 'rows';

  return (
    <div style={{ borderTop: `1px solid ${INK[100]}` }}>
      <button
        type='button'
        data-testid='mask-results-unit-row'
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%',
          padding: '14px 20px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
        }}
      >
        <span style={{ marginTop: 2, color: INK[400] }}>
          {open ? <BsChevronDown size={16} /> : <BsChevronRight size={16} />}
        </span>
        <span style={{ marginTop: 2, color: INK[400] }}>
          {isFile ? <BsFileEarmarkText size={16} /> : <BsTable size={16} />}
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <StatusChip unit={unit} units={units} />
            <span style={{ fontWeight: 600, color: INK[900], wordBreak: 'break-all' }}>{unit.name}</span>
          </div>
          {unit.errors.length > 0 && (
            <div style={{ marginTop: 4, fontSize: 13, color: STATUS.danger.text, wordBreak: 'break-word' }}>
              {unit.errors[0]}
              {unit.errors.length > 1 && (
                <span style={{ color: INK[400] }}> (+{fmtNum(unit.errors.length - 1)} more)</span>
              )}
            </div>
          )}
          <div style={{ marginTop: 4, fontSize: 14, color: INK[500] }}>
            {fmtNum(unit.masked)} {units} {verb}
            <span style={{ margin: '0 6px', color: INK[200] }}>·</span>
            {fmtNum(unit.fields.length)} field{unit.fields.length === 1 ? '' : 's'}
            {unit.failedRows > 0 && (
              <span style={{ color: FAIL_RED, fontWeight: 500 }}>
                <span style={{ margin: '0 6px', color: INK[200] }}>·</span>{fmtNum(unit.failedRows)} failed
              </span>
            )}
            {unit.skipped > 0 && (
              <span style={{ color: STATUS.warning.text }}>
                <span style={{ margin: '0 6px', color: INK[200] }}>·</span>{fmtNum(unit.skipped)} resumed-skipped
              </span>
            )}
          </div>
          {unit.algGroups.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {unit.algGroups.map((g) => <AlgChip key={g.alg} {...g} />)}
            </div>
          )}
        </div>
      </button>

      {open && (
        <div style={{ background: INK[50], padding: '4px 20px 20px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
              <BsSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: INK[400] }} />
              <input
                data-testid='filter-mask-results-unit-fields'
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder='Filter fields or algorithms…'
                style={{
                  width: '100%', borderRadius: 8, border: `1px solid ${INK[200]}`, background: '#fff',
                  padding: '6px 12px 6px 30px', fontSize: 14, outline: 'none',
                }}
              />
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              style={{ borderRadius: 8, border: `1px solid ${INK[200]}`, background: '#fff', padding: '6px 10px', fontSize: 14, color: INK[500] }}
            >
              <option value='field'>Sort: Field name</option>
              <option value='algorithm'>Sort: Algorithm</option>
            </select>
          </div>

          {unit.errors.length > 0 && (
            <div style={{ marginBottom: 12, borderRadius: 8, border: '1px solid #F6C9C9', background: '#fff', overflow: 'hidden' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                background: '#FDF2F2', borderBottom: `1px solid ${STATUS.danger.bg}`,
                fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: FAIL_RED,
              }}>
                <BsExclamationTriangle size={13} /> Failures ({fmtNum(unit.errors.length)})
              </div>
              <div>
                {unit.errors.map((line, i) => (
                  <div key={i} style={{ padding: '6px 12px', fontSize: 12, color: STATUS.danger.text, borderTop: i ? '1px solid #FDF2F2' : 'none' }}>
                    {line}
                  </div>
                ))}
              </div>
            </div>
          )}

          {filtered.length > 0 ? (
            <div style={{ borderRadius: 8, border: `1px solid ${INK[200]}`, background: '#fff', overflow: 'hidden' }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px',
                background: INK[50], borderBottom: `1px solid ${INK[100]}`,
                fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: INK[400],
              }}>
                <span>{verb.charAt(0).toUpperCase() + verb.slice(1)} fields ({fmtNum(filtered.length)})</span>
                <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>field · algorithm</span>
              </div>
              <div style={{ maxHeight: 420, overflowY: 'auto', padding: 8 }}>
                <div style={{ display: 'grid', gap: 6, gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
                  {shown.map((f) => (
                    <div key={f.field} style={{ borderRadius: 6, border: `1px solid ${INK[200]}`, background: '#fff', padding: '6px 8px' }}>
                      <div style={{ fontFamily: 'monospace', fontSize: 13, color: INK[700], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.field}
                      </div>
                      <div style={{ marginTop: 2, fontSize: 11, fontWeight: 600, color: algColor(f.algorithm), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.algorithm}
                      </div>
                    </div>
                  ))}
                </div>
                {filtered.length > MAX_FIELDS && (
                  <div style={{ marginTop: 8, textAlign: 'center', fontSize: 12, color: INK[500] }}>
                    Showing {fmtNum(MAX_FIELDS)} of {fmtNum(filtered.length)} masked fields — search to narrow
                  </div>
                )}
              </div>
            </div>
          ) : (
            unit.errors.length === 0 && (
              <div style={{ borderRadius: 8, border: `1px solid ${INK[200]}`, background: '#fff', padding: '24px', textAlign: 'center', fontSize: 14, color: INK[400] }}>
                {unit.fields.length === 0 ? 'No field plan available for this ' + (isFile ? 'file' : isNoSql ? 'collection' : 'table') + '.' : `No fields match “${q}”`}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

export default UnitRow;
